import React, { useState, useEffect, useRef } from 'react';
import {
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  RecaptchaVerifier,
  sendEmailVerification,
  type MultiFactorInfo,
  type TotpSecret,
  type User,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { ShieldCheck, Phone, KeyRound, Trash2, MailWarning } from 'lucide-react';

// Two second-factor types, both owned entirely by Firebase's own MFA APIs
// (Identity Platform) -- no custom backend for either. Phone requires a
// real reCAPTCHA per Firebase's phone-auth abuse protection; TOTP (an
// authenticator app like Google Authenticator/Authy) needs no reCAPTCHA,
// just a shared secret the user adds to their app and a rotating code
// back to prove they hold it.
//
// Both require a *verified* email first -- a real Identity Platform
// precondition ("Need to verify email first before enrolling second
// factors"), confirmed against the live project, not just documented
// behavior. Enrollment is hidden behind that gate below rather than left
// to fail with a confusing error.
type Step = 'idle' | 'choose' | 'phone-number' | 'phone-code' | 'totp-code';

export default function TwoFactorSetup({ user }: { user: User }) {
  const [factors, setFactors] = useState<MultiFactorInfo[]>([]);
  const [step, setStep] = useState<Step>('idle');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const verifierRef = useRef<RecaptchaVerifier | null>(null);

  useEffect(() => {
    setFactors(multiFactor(user).enrolledFactors);
  }, [user]);

  const getVerifier = () => {
    if (!verifierRef.current && recaptchaRef.current) {
      verifierRef.current = new RecaptchaVerifier(auth, recaptchaRef.current, { size: 'invisible' });
    }
    return verifierRef.current!;
  };

  const handleResendVerification = async () => {
    setError('');
    setLoading(true);
    try {
      await sendEmailVerification(user);
      setResendSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send verification email.');
    } finally {
      setLoading(false);
    }
  };

  const resetToIdle = () => {
    setFactors(multiFactor(user).enrolledFactors);
    setStep('idle');
    setPhoneNumber('');
    setCode('');
    setVerificationId(null);
    setTotpSecret(null);
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const session = await multiFactor(user).getSession();
      const provider = new PhoneAuthProvider(auth);
      const id = await provider.verifyPhoneNumber({ phoneNumber, session }, getVerifier());
      setVerificationId(id);
      setStep('phone-code');
    } catch (err: any) {
      if (err.code === 'auth/requires-recent-login') {
        setError('For your security, please sign out and back in, then try again.');
      } else {
        setError(err.message || 'Failed to send verification code.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhoneCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationId) return;
    setError('');
    setLoading(true);
    try {
      const cred = PhoneAuthProvider.credential(verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await multiFactor(user).enroll(assertion, 'Primary phone');
      resetToIdle();
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTotp = async () => {
    setError('');
    setLoading(true);
    try {
      const session = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      setTotpSecret(secret);
      setStep('totp-code');
    } catch (err: any) {
      if (err.code === 'auth/requires-recent-login') {
        setError('For your security, please sign out and back in, then try again.');
      } else {
        setError(err.message || 'Failed to start authenticator app setup.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotpCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!totpSecret) return;
    setError('');
    setLoading(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, code);
      await multiFactor(user).enroll(assertion, 'Authenticator app');
      resetToIdle();
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnenroll = async (factor: MultiFactorInfo) => {
    const label = factor.factorId === 'totp' ? 'this authenticator app' : 'this phone number';
    if (!window.confirm(`Remove ${label} from two-factor authentication?`)) return;
    setLoading(true);
    try {
      await multiFactor(user).unenroll(factor);
      setFactors(multiFactor(user).enrolledFactors);
    } catch (err: any) {
      setError(err.message || 'Failed to remove.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-emerald-600" />
        <h3 className="text-sm font-medium text-slate-900">Two-Factor Authentication</h3>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Add an authenticator app or phone number to require a second code on top of your password when you sign in.
      </p>

      {factors.length > 0 && (
        <div className="space-y-2 mb-4">
          {factors.map((f) => (
            <div key={f.uid} className="flex items-center justify-between bg-white p-3 rounded-md border border-slate-100">
              <div className="flex items-center gap-2">
                {f.factorId === 'totp' ? (
                  <KeyRound className="w-4 h-4 text-slate-500" />
                ) : (
                  <Phone className="w-4 h-4 text-slate-500" />
                )}
                <span className="text-sm text-slate-800">
                  {f.displayName || (f.factorId === 'totp' ? 'Authenticator app' : 'Phone')}
                  {f.factorId !== 'totp' && (f as any).phoneNumber ? ` (${(f as any).phoneNumber})` : ''}
                </span>
              </div>
              <button onClick={() => handleUnenroll(f)} disabled={loading} className="text-red-600 hover:text-red-700 disabled:opacity-50">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md p-2 mb-3">{error}</p>}

      {!user.emailVerified ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <MailWarning className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">Verify your email to enable two-factor authentication</span>
          </div>
          <p className="text-xs text-amber-700 mb-2">
            Firebase requires a verified email address on your account before enrolling a second factor.
          </p>
          <button
            onClick={handleResendVerification}
            disabled={loading || resendSent}
            className="px-3 py-1.5 bg-amber-600 text-white rounded-md text-xs font-medium hover:bg-amber-700 disabled:opacity-50"
          >
            {resendSent ? 'Verification email sent -- check your inbox' : loading ? 'Sending…' : 'Send verification email'}
          </button>
        </div>
      ) : (
        <>
          {step === 'idle' && (
            <button
              onClick={() => setStep('choose')}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
            >
              {factors.length > 0 ? 'Add another method' : 'Enable two-factor authentication'}
            </button>
          )}

          {step === 'choose' && (
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleStartTotp}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors"
              >
                <KeyRound className="w-4 h-4" /> Authenticator App
              </button>
              <button
                onClick={() => { setStep('phone-number'); setError(''); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                <Phone className="w-4 h-4" /> Text Message
              </button>
              <button type="button" onClick={() => setStep('idle')} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700">
                Cancel
              </button>
            </div>
          )}

          {step === 'phone-number' && (
            <form onSubmit={handleSendCode} className="space-y-3">
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1 555 123 4567"
                required
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <p className="text-xs text-slate-500">Use E.164 format (a leading + and country code).</p>
              <div className="flex gap-2">
                <button type="submit" disabled={loading} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
                  {loading ? 'Sending…' : 'Send code'}
                </button>
                <button type="button" onClick={() => { setStep('idle'); setError(''); }} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {step === 'phone-code' && (
            <form onSubmit={handleVerifyPhoneCode} className="space-y-3">
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6-digit code"
                required
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <div className="flex gap-2">
                <button type="submit" disabled={loading} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
                  {loading ? 'Verifying…' : 'Verify & enable'}
                </button>
                <button type="button" onClick={() => { setStep('idle'); setError(''); }} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {step === 'totp-code' && totpSecret && (
            <form onSubmit={handleVerifyTotpCode} className="space-y-3">
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <p className="text-xs text-slate-600 mb-2">
                  In your authenticator app (Google Authenticator, Authy, 1Password, etc.), add a new account and enter this key manually:
                </p>
                <p className="font-mono text-sm font-bold text-slate-900 tracking-wider break-all bg-slate-50 p-2 rounded border border-slate-100">
                  {totpSecret.secretKey}
                </p>
                <p className="text-xs text-slate-500 mt-2">Account name: {user.email} &middot; Issuer: Tabulario</p>
              </div>
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6-digit code from the app"
                required
                autoFocus
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <div className="flex gap-2">
                <button type="submit" disabled={loading} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
                  {loading ? 'Verifying…' : 'Verify & enable'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('idle'); setError(''); setTotpSecret(null); setCode(''); }}
                  className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}

      {/* Invisible reCAPTCHA container -- required by Firebase's phone-auth
          abuse protection; renders nothing visible in the 'invisible' size.
          TOTP enrollment doesn't use it, but it's harmless to keep mounted. */}
      <div ref={recaptchaRef} />
    </div>
  );
}
